/**
 * useSectionCache — preserves section builder state across navigations.
 *
 * Encapsulates the pattern duplicated across all 4 Builder components:
 *   1. Restore cached data on mount
 *   2. Save data to cache on unmount
 */
import { useState, useEffect, useRef } from 'react';
import { getSectionCache, setSectionCache } from '@/lib/sectionCache';

/**
 * @param cacheKey - Unique key for this section's cache slot.
 * @returns [cachedData, setCachedData] — use setCachedData when new data arrives.
 *          On unmount, the current value is automatically saved to the cache.
 */
export function useSectionCache<T>(cacheKey: string): [T | null, (data: T) => void] {
  const [cachedData, setCachedData] = useState<T | null>(
    () => getSectionCache<T>(cacheKey),
  );
  const dataRef = useRef(cachedData);

  // Keep ref in sync
  useEffect(() => {
    dataRef.current = cachedData;
  }, [cachedData]);

  // Save to cache on unmount
  useEffect(() => {
    return () => {
      if (dataRef.current != null) {
        setSectionCache(cacheKey, dataRef.current);
      }
    };
  }, [cacheKey]);

  return [cachedData, setCachedData];
}
